import { useState, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { Controller, type Control, type FieldErrors, type UseFormSetError, type UseFormClearErrors } from "react-hook-form";
import Select from "react-select";
import type { NewDeviceFormType } from "../types";
import { getCustomSelectStyles } from "../../../../shared";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { checkTrailerPlateAvailable } from "../../../../../../features/devices/mutations";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import {
  getTrailerTypeOptions,
  getTrailerConditionOptions,
  getTrailerBrakeTypeOptions,
  getTrailerSuspensionTypeOptions,
} from "../utils/inlineEntityOptions";

interface Props {
  control: Control<NewDeviceFormType>;
  errors: FieldErrors<NewDeviceFormType>;
  register: any;
  isDarkMode: boolean;
  getInputClassName: (hasError: boolean) => string;
  setError: UseFormSetError<NewDeviceFormType>;
  clearErrors: UseFormClearErrors<NewDeviceFormType>;
}

export function TrailerInlineForm({
  control,
  errors,
  register,
  isDarkMode,
  getInputClassName,
  setError,
  clearErrors,
}: Props) {
  const { t } = useTranslation("adminSections");
  const { isRequired } = useSchemaOverrides("remolques", "postRemolque");
  const { statuses } = useStatusesStore();
  const selectedCompanyId = useCompaniesStore((s) => s.companyId);
  const statusCompanyId = selectedCompanyId || localStorage.getItem("userCompanyId");
  const [checkingPlate, setCheckingPlate] = useState(false);

  const trailerTypeOptions = getTrailerTypeOptions(t);
  const trailerConditionOptions = getTrailerConditionOptions(t);
  const trailerBrakeTypeOptions = getTrailerBrakeTypeOptions(t);
  const trailerSuspensionTypeOptions = getTrailerSuspensionTypeOptions(t);
  const trailerStatusOptions = getStatusOptionsForEntity(statuses, "trailers", t, statusCompanyId);
  const errorClassName = "text-red-500 text-xs mt-1";
  const labelClass = `text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"} mb-1`;

  const handlePlateBlur = async (e: FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();
    if (!trimmed) { clearErrors("remolque_plate"); return; }
    setCheckingPlate(true);
    try {
      const result = await checkTrailerPlateAvailable(trimmed);
      if (!result.available) {
        setError("remolque_plate", { type: "manual", message: result.message! });
      } else {
        clearErrors("remolque_plate");
      }
    } finally {
      setCheckingPlate(false);
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.name", { defaultValue: "Nombre" })} {isRequired("name") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("trailers.placeholders.name", { defaultValue: "Ej. Caja seca #12" })}
          className={getInputClassName(!!errors.remolque_name)}
          {...register("remolque_name", {
            required: isRequired("name") ? t("trailers.validation.nameRequired") : false,
          })}
        />
        {errors.remolque_name && <span className={errorClassName}>{errors.remolque_name.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.plate", { defaultValue: "Placa" })} {isRequired("plate") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("trailers.placeholders.plate", { defaultValue: "Ej. REM-5678" })}
          className={getInputClassName(!!errors.remolque_plate)}
          {...register("remolque_plate", {
            required: isRequired("plate") ? t("trailers.validation.plateRequired") : false,
            minLength: { value: 6, message: t("units.form.minLength6") },
            maxLength: { value: 8, message: t("units.form.maxLength") },
          })}
          onBlur={handlePlateBlur}
        />
        {checkingPlate && (
          <span className="text-xs text-gray-400">{t("devices.validation.checkingPlate", { defaultValue: "Verificando placa..." })}</span>
        )}
        {errors.remolque_plate && <span className={errorClassName}>{errors.remolque_plate.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.typeTitle", { defaultValue: "Tipo de remolque" })} {isRequired("type") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="remolque_type"
          control={control}
          rules={{ required: isRequired("type") ? t("trailers.validation.typeRequired") : false }}
          render={({ field }) => (
            <Select
              options={trailerTypeOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_type)}
              placeholder={t("trailers.placeholders.selectType", { defaultValue: "Selecciona tipo" })}
              value={trailerTypeOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "dry_van")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.remolque_type && <span className={errorClassName}>{errors.remolque_type.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.brand", { defaultValue: "Marca" })} {isRequired("brand") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("trailers.placeholders.brand", { defaultValue: "Ej. Utility, Wabash" })}
          className={getInputClassName(!!errors.remolque_brand)}
          {...register("remolque_brand", {
            required: isRequired("brand") ? t("trailers.validation.brandRequired") : false,
          })}
        />
        {errors.remolque_brand && <span className={errorClassName}>{errors.remolque_brand.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.year", { defaultValue: "Año" })} {isRequired("year") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="number"
          min={1900}
          max={2100}
          placeholder={t("trailers.placeholders.year", { defaultValue: "Ej. 2023" })}
          className={getInputClassName(!!errors.remolque_year)}
          {...register("remolque_year", {
            required: isRequired("year") ? t("trailers.validation.yearRequired") : false,
          })}
        />
        {errors.remolque_year && <span className={errorClassName}>{errors.remolque_year.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.status", { defaultValue: "Estado" })} {isRequired("status_id") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="remolque_status_id"
          control={control}
          rules={{ required: isRequired("status_id") ? t("trailers.validation.statusRequired") : false }}
          render={({ field }) => (
            <Select
              options={trailerStatusOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_status_id)}
              placeholder={t("trailers.placeholders.selectStatus", { defaultValue: "Selecciona estado" })}
              value={trailerStatusOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.remolque_status_id && <span className={errorClassName}>{errors.remolque_status_id.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.condition", { defaultValue: "Condición" })} {isRequired("condition") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="remolque_condition"
          control={control}
          rules={{ required: isRequired("condition") ? t("trailers.validation.conditionRequired") : false }}
          render={({ field }) => (
            <Select
              options={trailerConditionOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_condition)}
              placeholder={t("trailers.placeholders.selectCondition", { defaultValue: "Selecciona condición" })}
              value={trailerConditionOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.remolque_condition && <span className={errorClassName}>{errors.remolque_condition.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.capacity", { defaultValue: "Capacidad" })} {isRequired("capacity") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("trailers.placeholders.capacity", { defaultValue: "Ej. 20 ton" })}
          className={getInputClassName(!!errors.remolque_capacity)}
          {...register("remolque_capacity", {
            required: isRequired("capacity") ? t("trailers.validation.capacityRequired") : false,
          })}
        />
        {errors.remolque_capacity && <span className={errorClassName}>{errors.remolque_capacity.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.maximumWeight", { defaultValue: "Peso máximo" })} {isRequired("maximumWeight") && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          placeholder={t("trailers.placeholders.maximumWeight", { defaultValue: "Ej. 25000 kg" })}
          className={getInputClassName(!!errors.remolque_maximumWeight)}
          {...register("remolque_maximumWeight", {
            required: isRequired("maximumWeight") ? t("trailers.validation.maximumWeightRequired") : false,
          })}
        />
        {errors.remolque_maximumWeight && <span className={errorClassName}>{errors.remolque_maximumWeight.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.brakeType", { defaultValue: "Tipo de frenos" })} {isRequired("brakeType") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="remolque_brakeType"
          control={control}
          rules={{ required: isRequired("brakeType") ? t("trailers.validation.brakeTypeRequired") : false }}
          render={({ field }) => (
            <Select
              options={trailerBrakeTypeOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_brakeType)}
              placeholder={t("trailers.placeholders.selectBrakeType", { defaultValue: "Selecciona tipo de frenos" })}
              value={trailerBrakeTypeOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.remolque_brakeType && <span className={errorClassName}>{errors.remolque_brakeType.message}</span>}
      </div>

      <div className="flex flex-col">
        <label className={labelClass}>
          {t("trailers.fields.suspensionType", { defaultValue: "Tipo de suspensión" })} {isRequired("suspensionType") && <span className="text-red-500">*</span>}
        </label>
        <Controller
          name="remolque_suspensionType"
          control={control}
          rules={{ required: isRequired("suspensionType") ? t("trailers.validation.suspensionTypeRequired") : false }}
          render={({ field }) => (
            <Select
              options={trailerSuspensionTypeOptions}
              styles={getCustomSelectStyles(isDarkMode, !!errors.remolque_suspensionType)}
              placeholder={t("trailers.placeholders.selectSuspensionType", { defaultValue: "Selecciona suspensión" })}
              value={trailerSuspensionTypeOptions.find((o) => o.value === field.value) || null}
              onChange={(opt: any) => field.onChange(opt?.value || "")}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.remolque_suspensionType && <span className={errorClassName}>{errors.remolque_suspensionType.message}</span>}
      </div>
    </>
  );
}
